// src/pages/BedMaster.jsx
import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Snackbar,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  TablePagination,
  IconButton,
  Box,
  TableContainer,
  Paper,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { API_BASE_URL } from "../../../constant/constant";
import { decrypt } from "../../../components/cryptoUtil";
import { Heading } from "../../componenet/heading";

// Sorting helper functions
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  const stabilized = array.map((el, idx) => [el, idx]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

const BedMaster = () => {
  const [bedTypes, setBedTypes] = useState([]);
  const [wards, setWards] = useState([]);
  const [beds, setBeds] = useState([]);
  const [bedStatusOptions, setBedStatusOptions] = useState([]);

  const [wardId, setWardId] = useState("");
  const [bedType, setBedType] = useState("");
  const [bedCode, setBedCode] = useState("");
  const [bedCharge, setBedCharge] = useState("");
  const [bedStatus, setBedStatus] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("ward_name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  let decryptedData = {};
  try {
    const tokenData = localStorage.getItem("tokenData");
    decryptedData = tokenData ? JSON.parse(decrypt(tokenData)) : {};
  } catch (e) {
    console.warn("Failed to decrypt tokenData", e);
    decryptedData = {};
  }
  const token = localStorage.getItem("token") || "";
  const hospitalId = decryptedData.softsubgroup || "";

  // ------------------ Fetch data ------------------
  useEffect(() => {
    const fetchWards = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/hospital/wards/${hospitalId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (Array.isArray(data)) setWards(data);
      } catch (err) {
        console.error(err);
      }
    };

    const fetchBeds = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/hospital/beds/${hospitalId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (Array.isArray(data)) setBeds(data);
      } catch (err) {
        console.error(err);
      }
    };

    const fetchBedTypes = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/hospital/bedtypes/${hospitalId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (Array.isArray(data)) setBedTypes(data);
      } catch (err) {
        console.error(err);
      }
    };

    const fetchBedStatusOptions = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/hospital/bed-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setBedStatusOptions(data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchWards();
    fetchBeds();
    fetchBedTypes();
    fetchBedStatusOptions();
  }, [hospitalId, token]);

  // ------------------ CRUD operations ------------------
  const handleSubmit = async () => {
    if (!wardId || !bedType || !bedCode || !bedCharge || !bedStatus) {
      setSnackbar({ open: true, message: "All fields are required", severity: "error" });
      return;
    }

    try {
      const payload = { wardId, bedType, bedCode, bedCharge, hospitalId, bedStatus };
      const url = editingId
        ? `${API_BASE_URL}/api/hospital/updatebed/${editingId}`
        : `${API_BASE_URL}/api/hospital/addbed`;
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        setSnackbar({ open: true, message: data.message || "Saved successfully", severity: "success" });
        setWardId(""); setBedType(""); setBedCode(""); setBedCharge(""); setBedStatus(""); setEditingId(null);
        const updatedBeds = await fetch(`${API_BASE_URL}/api/hospital/beds/${hospitalId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBeds(await updatedBeds.json());
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to save", severity: "error" });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Error occurred", severity: "error" });
    }
  };

  const handleEdit = (bed) => {
    setEditingId(bed.bed_id);
    setWardId(bed.ward_id);
    setBedType(bed.bedtype_id);
    setBedCode(bed.bed_code);
    setBedCharge(bed.bed_charge);
    setBedStatus(bed.bed_status);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this bed?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/hospital/deletebed/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setBeds(beds.filter((b) => b.bed_id !== id));
        setSnackbar({ open: true, message: data.message || "Deleted successfully", severity: "success" });
      } else {
        setSnackbar({ open: true, message: data.message || "Delete failed", severity: "error" });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Error occurred", severity: "error" });
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = stableSort(beds, getComparator(order, orderBy)).slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ------------------ Render ------------------
  return (
    <Card>
      <CardContent>
   <Heading headingtext="🛏️ Bed Master" />


        {/* --- Form Layout --- */}

        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: 8 }}>
          <div style={{ flex: 1, minWidth: "250px" }}>
            <TextField
              select
              label="Select Ward"
              value={wardId}
              onChange={(e) => setWardId(e.target.value)}
              fullWidth
            >
              {wards.map((w) => <MenuItem key={w.ward_id} value={w.ward_id}>{w.ward_name}</MenuItem>)}
            </TextField>
          </div>
          <div style={{ flex: 1, minWidth: "250px" }}>
            <TextField
              select
              label="Bed Type"
              value={bedType}
              onChange={(e) => setBedType(e.target.value)}
              fullWidth
            >
              {bedTypes.map((bt) => <MenuItem key={bt.bedtype_id} value={bt.bedtype_id}>{bt.bedtype_name}</MenuItem>)}
            </TextField>
          </div>
        </div>
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: 8 }}>
          <div style={{ flex: 1, minWidth: "250px" }}>
            <TextField
              label="Bed Code"
              value={bedCode}
              onChange={(e) => setBedCode(e.target.value)}
              fullWidth
            />
          </div>
          <div style={{ flex: 1, minWidth: "250px" }}>
            <TextField
              label="Bed Charge"
              type="number"
              value={bedCharge}
              onChange={(e) => setBedCharge(e.target.value)}
              fullWidth
            />
          </div>
        </div>


        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: 8 }}>
          <div style={{ flex: 1, minWidth: "250px" }}>
            <TextField
              select
              label="Bed Status"
              value={bedStatus}
              onChange={(e) => setBedStatus(e.target.value)}
              fullWidth
              style={{ marginTop: 16 }}
            >
              {bedStatusOptions.map((status, idx) => <MenuItem key={idx} value={status}>{status}</MenuItem>)}
            </TextField>
          </div>


          <div style={{ flex: 1, minWidth: "250px" }}>
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
               // color="red"
                onClick={handleSubmit}
                sx={{ height: 50, backgroundColor: "brown" ,color:"white"}}
              >
                {editingId ? "✔️ Update Bed" : "➕ Add Bed"}
              </Button>
            </Box>

          </div>


        </div>




        {/* --- Beds Table --- */}
        <TableContainer component={Paper} sx={{ mt: 8, maxHeight: 400, overflowX: "auto" }}>
          <Table >
            <TableHead sx={{ backgroundColor: "#106bb5ff", color: "#fff" }}>
              <TableRow >
                {["ward_name", "bedtype_name", "bed_code", "bed_charge", "bed_status"].map((head) => (
                  <TableCell key={head} sx={{ color: "#fff", fontWeight: "bold" }}>
                    <TableSortLabel
                      active={orderBy === head}
                      direction={orderBy === head ? order : "asc"}
                      onClick={() => handleRequestSort(head)}
                    >
                      {head.replace("_", " ").toUpperCase()}
                    </TableSortLabel>
                  </TableCell>
                ))}
                {(decryptedData.userrole === 999 || decryptedData.userrole === 9) && (
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>ACTIONS</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((b) => (
                <TableRow key={b.bed_id} hover>
                  <TableCell>{b.ward_name}</TableCell>
                  <TableCell>{b.bedtype_name}</TableCell>
                  <TableCell>{b.bed_code}</TableCell>
                  <TableCell>{b.bed_charge}</TableCell>
                  <TableCell>{b.bed_status}</TableCell>
                  {(decryptedData.userrole === 999 || decryptedData.userrole === 9) && (
                    <TableCell>
                      <IconButton onClick={() => handleEdit(b)}><Edit /></IconButton>
                      <IconButton onClick={() => handleDelete(b.bed_id)} color="error"><Delete /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={beds.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        {/* Snackbar */}
        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default BedMaster;
